import './info_panel.asset.less';

import React, { Fragment } from 'react';
import classNames from 'classnames';
import isNil from 'lodash/isNil';
import includes from 'lodash/includes';

import Button from 'zoomforth_component_lib/btn_basic.jsx';
import ItemPath from './info_panel.item_path.jsx';
import Tipsy from 'zoomforth_component_lib/tipsy.jsx';
import InputContainer from 'zoomforth_component_lib/input_container.jsx';
import InputContainerWithInlineEditing from 'zoomforth_component_lib/input_container_with_inline_editing.jsx';
import FroalaDescriptionEditor from './froala_description_editor.jsx';
import TextInput from 'zoomforth_component_lib/basic_text_input.jsx';
import ReplaceAssetModal from './replace_asset/replace_asset_modal_container.jsx';
import AssetSiteListModal from './replace_asset/site_list_modal.jsx';
import CircularSpinner from 'zoomforth_component_lib/circular_spinner.jsx';
import { EditIcon } from 'zoomforth_component_lib/app_modern_icons';
import { PlainCheckbox } from 'zoomforth_component_lib/checkbox.jsx';

const InfoPanelAssetUI = ({
  asset,
  assetType,
  assetDownloadUrl,
  currentFolder,
  pathAsFolderData,
  createdAt,
  description,
  altText,
  displayImageUrl,
  isFilenameUsedAsTitle,
  isInSearchMode,
  isPlayable,
  lastModifiedAt,
  name,
  subtitles,
  siteList,
  uploadedByUserDisplayName,
  didSiteListFetchError,
  shouldShowReplaceAssetModal,
  shouldShowAssetSiteListModal,
  onAssetInfoAreaRefAttach,
  onChooseVideoThumbnailClick,
  onDeleteClick,
  onDeleteVideoSubtitlesClick,
  onExpandClick,
  onFolderNavigate,
  onIsFilenameUsedAsTitleChange,
  onMoveClick,
  onDescriptionSave,
  onRenameSave,
  onShouldShowAssetSiteListModalChange,
  onShouldShowReplaceAssetModal,
  onAssetReplace,
  onClearAssetSelection,
  onAltTextSave
}) => {
  const hasDefaultDescription = !includes(['ppt', 'pdf', 'other'], assetType);
  const isReplacable = includes([
    'video',
    'photo',
    'pdf',
    'ppt',
    'html_package',
    'other'
  ], assetType);
  const canHaveSubtitles = includes(['youtube', 'video'], assetType);

  return (
    <div id="media-library-info-panel-asset">
      {shouldShowReplaceAssetModal && (
        <ReplaceAssetModal
          assetModel={asset}
          siteList={siteList}
          onClose={() => onShouldShowReplaceAssetModal(false)}
          onAssetReplace={onAssetReplace}
          currentFolder={currentFolder}
          onClearAssetSelection={onClearAssetSelection}
        />
      )}
      {shouldShowAssetSiteListModal && (
        <AssetSiteListModal
          siteList={siteList}
          onClose={() => onShouldShowAssetSiteListModalChange(false)}
        />
      )}
      <div className="preview-img">
        <div
          className={classNames('preview-wrapper', { clickable: assetType !== 'other' })}
          style={{ backgroundImage: `url('${displayImageUrl}')` }}
          onClick={assetType === 'other' ? undefined : onExpandClick}
        >
          {assetType !== 'other' &&
            <div
              className={classNames('action-expand expand-preview', { playable: isPlayable })}
            >
              <i className={classNames('fa', isPlayable ? 'fa-play' : 'fa-expand')} />
            </div>
          }
        </div>
      </div>
      <div className="asset-header" ref={onAssetInfoAreaRefAttach}>

        <InputContainerWithInlineEditing
          className="asset-name"
          value={name || ''}
          getErrorMessageForValue={(value) => value ? null : 'Must not be blank'}
          shouldRenderEditButton={false}
          onSave={onRenameSave}
          render={({ value, isEditing, onEdit, onValueChange, onSave }) =>
            isEditing ?
              <Fragment>
                <div className="asset-label">Name</div>
                <TextInput
                  tellRef={(ref) => { if (ref) { ref.focus(); } }}
                  value={value}
                  onChange={(e) => { onValueChange(e.target.value); }}
                  onEnterKeyUp={onSave}
                />
              </Fragment>
              : value ?
                <Fragment>
                  <div className="asset-label">Name</div>
                  <div onClick={onEdit}>
                    <div className="asset-value asset-name-html" dangerouslySetInnerHTML={{ __html: value }} />
                  </div>
                </Fragment>
                :
                <Fragment>
                  <div className="asset-label">Name</div>
                  <button className="asset-value placeholder container-button" onClick={onEdit}>Click to add a title</button>
                </Fragment>
          }
        />
        {hasDefaultDescription &&
          <InputContainerWithInlineEditing
            className={classNames('asset-description', description && 'with-value')}
            value={description || ''}
            editButtonText={description ? 'Edit Description' : 'Add Description'}
            onSave={onDescriptionSave}
            shouldRenderEditButton={false}
            render={({ value, isEditing, onEdit, onValueChange }) =>
              isEditing ?
                <Fragment>
                  <div className="asset-label">Description</div>
                  <FroalaDescriptionEditor
                    model={value}
                    onModelChange={onValueChange}
                  />
                </Fragment>
                : value ?
                  <Fragment>
                    <div className="asset-label">Description</div>
                    <div onClick={onEdit}>
                      <div className="asset-value" dangerouslySetInnerHTML={{ __html: value }} />
                    </div>
                  </Fragment>
                  :
                  <Fragment>
                    <div className="asset-label">Description</div>
                    <button className="asset-value placeholder container-button" onClick={onEdit}>Click to add a description</button>
                  </Fragment>
            }
          />
        }
        {!isNil(displayImageUrl) &&
          <InputContainerWithInlineEditing
            className="asset-alt-text"
            value={altText || null}
            editButtonText={altText ? 'Edit Alternative Text' : 'Add Alternative Text'}
            onSave={onAltTextSave}
            shouldRenderEditButton={false}
            render={({ value, isEditing, onEdit, onValueChange }) =>
              isEditing ?
                <InputContainer
                  name="Alt Text"
                  tooltip="Short description of the image. It is read aloud to users by screen reader software and is displayed instead of the image if it fails to load."
                  value={value}
                  onChange={e => onValueChange(e.target.value)}
                />
                : !isNil(value) ?
                  <Fragment>
                    <Tipsy placement="left" content="Short description of the image. It is read aloud to users by screen reader software and is displayed instead of the image if it fails to load.">
                      <div className="asset-label">Alt Text</div>
                    </Tipsy>
                    <div className="asset-value" onClick={onEdit}>
                      {value}
                    </div>
                  </Fragment>
                  :
                  <Fragment>
                    <Tipsy placement="left" content="Short description of the image. It is read aloud to users by screen reader software and is displayed instead of the image if it fails to load.">
                      <div className="asset-label">Alt Text</div>
                    </Tipsy>
                    <button className="asset-value placeholder container-button" onClick={onEdit}>Click to add alt text</button>
                  </Fragment>
            }
          />
        }
        {hasDefaultDescription &&
          <div className="asset-defaults-toggle">
            <PlainCheckbox
              id="asset-defaults-toggle-checkbox"
              checked={isFilenameUsedAsTitle}
              onChange={() => { onIsFilenameUsedAsTitleChange(!isFilenameUsedAsTitle); }}
            />
            <label htmlFor="asset-defaults-toggle-checkbox">
              <Tipsy placement="left" content="The title and description set will automatically appear in the sites where this asset is in added.">
                <span>Set name and description as default</span>
              </Tipsy>
            </label>
          </div>
        }
        {(assetType === 'video' || canHaveSubtitles || !isNil(displayImageUrl)) &&
          <div className="extra-actions">
            {assetType === 'video' &&
              <div className="extra-label action-choose-video-thumbnail" onClick={onChooseVideoThumbnailClick}>
                <i className="fa fa-picture-o"></i>
                <span>Change Image</span>
              </div>
            }
            {canHaveSubtitles &&
              <div className="video-subtitles action-set-video-subtitles">
                <label className="extra-label">
                  <i className="fa fa-cc" />
                  <span>
                    {
                      subtitles ?  'Change subtitles' : 'Upload subtitles'
                    }
                  </span>
                  <input className="hidden" type="file" name="file" />
                </label>

                {
                  subtitles &&
                    <Button
                      secondary
                      className="action-delete-video-subtitles"
                      onClick={onDeleteVideoSubtitlesClick}
                    >
                      Delete
                    </Button>
                }
              </div>
            }
          </div>
        }
      </div>
      <div className="action-buttons">
        <Button secondary onClick={onMoveClick}>Move</Button>
        {assetDownloadUrl &&
          <a className="btn-basic btn-basic-secondary" href={assetDownloadUrl}>Download</a>
        }
        {isReplacable &&
          <Button secondary disabled={!siteList || siteList.length == 0} onClick={() => onShouldShowReplaceAssetModal(true)}>Replace</Button>
        }
        <Button className="danger" secondary onClick={onDeleteClick}>Delete</Button>
      </div>
      {isInSearchMode &&
        <ItemPath
          className="asset-folder"
          pathAsFolderData={pathAsFolderData}
          onFolderNavigate={onFolderNavigate}
        />
      }
      <div className="asset-details">
        <div className="asset-details-col left">
          <div className="asset-detail">
            <div className="asset-label">Date added</div>
            <div className="asset-value">{createdAt}</div>
          </div>
          <div className="asset-detail">
            <div className="asset-label">Last Modified</div>
            <div className="asset-value">{lastModifiedAt}</div>
          </div>
          {uploadedByUserDisplayName &&
            <div className="asset-detail">
              <div className="asset-label">Uploaded By</div>
              <div className="asset-value">{uploadedByUserDisplayName}</div>
            </div>
          }
        </div>
        <div className="asset-details-col right">
          <div className="asset-detail">
            <div className="asset-label">Used On</div>
            {!siteList ?
              <div className="asset-value loading-indicator">
                <CircularSpinner width={10} height={10} />
              </div>
              : didSiteListFetchError ?
                <div>Error</div>
                : siteList.length == 0 ?
                  <div className="asset-value">{siteList.length} sites</div>
                  :
                  <div className="asset-value" id="site-count">
                    <a href="#" onClick={() => onShouldShowAssetSiteListModalChange(true)}>
                      {siteList.length} sites
                    </a>
                  </div>
            }
          </div>
        </div>
      </div>
    </div>
  );
};


export default InfoPanelAssetUI;
